import { checkPermissionByKey } from "../utils/checkPermissionByKey";
import { DEFAULT_DATE_RANGE_KEY, formatShortMonthDay, getDateRangeForPreset } from "../utils/dateRangePresets";
import { getConfigWithToken } from "../config/config";
import { getDashboardMetrics } from "../config/url";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import classes from "./Dashboard.module.css";
import DateRangeFilter from "../components/dashboard/DateRangeFilter";
import MetricCard from "../components/dashboard/MetricCard";

const emptyCommunity = {
	averageDurationMinutes: 0,
	flagged: 0,
	flaggedRate: 0,
	totalVisits: 0,
	verificationRate: 0,
	verified: 0,
};

const emptyEhr = {
	dataMismatch: 0,
	hasUpload: false,
	lastUploadDate: null,
	matchedWithIssues: 0,
	missingInTeleTrace: 0,
	totalEhrVisits: 0,
	unableToValidate: 0,
	unconfirmed: 0,
	verified: 0,
	verifiedRate: 0,
};

const emptyForms = {
	pendingSignature: 0,
	signatureRate: 0,
	signed: 0,
	totalForms: 0,
};

const toCount = (value) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeCommunityVisits = (data = {}) => {
	const verified = toCount(data.verified);
	const flagged = toCount(data.flagged);
	const totalVisits = toCount(data.totalVisits);

	return {
		averageDurationMinutes: toCount(data.averageDurationMinutes),
		flagged,
		flaggedRate: toCount(data.flaggedRate),
		totalVisits: totalVisits || verified + flagged,
		verificationRate: toCount(data.verificationRate),
		verified,
	};
};

const Dashboard = () => {
	const navigate = useNavigate();
	const user = useSelector((state) => state?.user);
	const token = user?.user?.token || localStorage.getItem("user_id");
	const organizationId =
		user?.user?.organizationId || localStorage.getItem("organizationId") || localStorage.getItem("businessId");

	const [presetKey, setPresetKey] = useState(DEFAULT_DATE_RANGE_KEY);
	const [customStart, setCustomStart] = useState(null);
	const [customEnd, setCustomEnd] = useState(null);
	const [range, setRange] = useState(() => getDateRangeForPreset(DEFAULT_DATE_RANGE_KEY));
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);
	const [communityVisits, setCommunityVisits] = useState(emptyCommunity);
	const [ehrValidation, setEhrValidation] = useState(emptyEhr);
	const [encounterForms, setEncounterForms] = useState(emptyForms);

	const requestConfig = useMemo(
		() => ({
			headers: {
				Accept: "application/json",
				"Cache-Control": "no-cache",
				"Content-Type": "application/json",
				...(token ? getConfigWithToken(token).headers : {}),
				...(organizationId ? { "x-organization-id": organizationId } : {}),
			},
		}),
		[organizationId, token],
	);

	const fetchMetrics = useCallback(async () => {
		if (!organizationId || !range.startDate || !range.endDate) {
			setIsLoading(false);
			setCommunityVisits(emptyCommunity);
			setEhrValidation(emptyEhr);
			setEncounterForms(emptyForms);
			return;
		}
		setIsLoading(true);
		setHasError(false);
		try {
			const response = await axios.get(getDashboardMetrics, {
				...requestConfig,
				params: {
					endDate: range.endDate,
					organizationId,
					startDate: range.startDate,
				},
			});
			const data = response?.data?.data || {};
			setCommunityVisits(normalizeCommunityVisits(data.communityVisits));
			setEhrValidation(data.ehrValidation || emptyEhr);
			setEncounterForms(data.encounterForms || emptyForms);
		} catch {
			setHasError(true);
			setCommunityVisits(emptyCommunity);
			setEhrValidation(emptyEhr);
			setEncounterForms(emptyForms);
		} finally {
			setIsLoading(false);
		}
	}, [organizationId, range.endDate, range.startDate, requestConfig]);

	useEffect(() => {
		fetchMetrics();
	}, [fetchMetrics]);

	const handleRangeChange = ({ customEnd: nextEnd, customStart: nextStart, presetKey: nextKey, range: nextRange }) => {
		setPresetKey(nextKey);
		setCustomStart(nextStart);
		setCustomEnd(nextEnd);
		setRange(nextRange);
	};

	const lastUploadLabel = formatShortMonthDay(ehrValidation.lastUploadDate);
	const periodLabel =
		presetKey === "custom" && customStart && customEnd
			? `${new Date(`${customStart}T00:00:00`).toLocaleDateString("en-US", {
					day: "2-digit",
					month: "2-digit",
					year: "numeric",
				})} - ${new Date(`${customEnd}T00:00:00`).toLocaleDateString("en-US", {
					day: "2-digit",
					month: "2-digit",
					year: "numeric",
				})}`
			: range.label || "This Month";
	const canView = checkPermissionByKey(user, "viewDashboard", "ViewDashboardListAccess");

	if (!canView) return null;

	return (
		<div className={classes.page}>
			<div className={classes.headerRow}>
				<div className={classes.titleBlock}>
					<h1 className={classes.title}>Dashboard</h1>
					<p className={classes.subtitle}>Monitor service verification and compliance activity.</p>
				</div>
				<DateRangeFilter
					customEnd={customEnd}
					customStart={customStart}
					onChange={handleRangeChange}
					presetKey={presetKey}
				/>
			</div>

			{hasError ? (
				<div className={classes.errorState}>
					<p className={classes.errorTitle}>Dashboard data could not be loaded</p>
					<button className={classes.retryButton} onClick={fetchMetrics} type="button">
						Try Again
					</button>
				</div>
			) : (
				<>
					<section className={classes.section}>
						<div className={classes.sectionHeading}>
							<div className={classes.sectionIcon} aria-hidden="true">
								<svg fill="none" height="16" viewBox="0 0 24 24" width="16">
									<path
										d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z"
										fill="#ffffff"
									/>
								</svg>
							</div>
							<h2 className={classes.sectionTitle}>Community Visit Verification</h2>
						</div>
						{!isLoading && communityVisits.totalVisits === 0 ? (
							<p className={classes.emptyHint}>No visits recorded during this period.</p>
						) : null}
						<div className={classes.gridFour}>
							<MetricCard
								isLoading={isLoading}
								onClick={() => navigate("/visits")}
								supportingText={periodLabel}
								title="Total Visits"
								value={communityVisits.totalVisits}
							/>
							<MetricCard
								isLoading={isLoading}
								onClick={() => navigate("/visits?status=Verified")}
								supportingText={`${communityVisits.verificationRate}% verification rate`}
								title="Verified"
								value={communityVisits.verified}
							/>
							<MetricCard
								isLoading={isLoading}
								onClick={() => navigate("/visits?status=Flagged")}
								supportingText={`${communityVisits.flaggedRate}% of total visits`}
								title="Flagged"
								value={communityVisits.flagged}
							/>
							<MetricCard
								isLoading={isLoading}
								onClick={() => navigate("/visits")}
								supportingText="Per verified visit"
								title="Avg. Duration"
								value={`${communityVisits.averageDurationMinutes}m`}
							/>
						</div>
					</section>

					<section className={classes.section}>
						<div className={classes.sectionHeading}>
							<div className={classes.sectionIcon} aria-hidden="true">
								<svg fill="none" height="16" viewBox="0 0 24 24" width="16">
									<path
										d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2Zm-9 14-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9Z"
										fill="#ffffff"
									/>
								</svg>
							</div>
							<h2 className={classes.sectionTitle}>EHR Validation</h2>
							{lastUploadLabel ? (
								<span className={classes.uploadBadge}>Last upload {lastUploadLabel}</span>
							) : null}
						</div>

						{!isLoading && !ehrValidation.hasUpload ? (
							<div className={classes.ehrEmpty}>
								<p className={classes.ehrEmptyTitle}>No EHR data uploaded</p>
								<p className={classes.ehrEmptyText}>
									Upload an EHR visit report from the Validate page to populate validation metrics.
								</p>
							</div>
						) : null}

						<div className={classes.gridFive}>
							<MetricCard
								isLoading={isLoading}
								onClick={() => navigate("/validate")}
								supportingText={lastUploadLabel ? `Last upload: ${lastUploadLabel}` : periodLabel}
								title="Total EHR Visits"
								value={ehrValidation.totalEhrVisits}
							/>
							<MetricCard
								isLoading={isLoading}
								onClick={() => navigate("/validate?status=Verified")}
								supportingText={`${ehrValidation.verifiedRate}% of uploaded records`}
								title="Verified"
								value={ehrValidation.verified}
							/>
							<MetricCard
								isLoading={isLoading}
								onClick={() => navigate("/validate?status=Matched%20with%20Issues")}
								supportingText="Flags present"
								title="Matched with Issues"
								value={ehrValidation.matchedWithIssues}
							/>
							<MetricCard
								isLoading={isLoading}
								onClick={() => navigate("/validate?status=Data%20Mismatch")}
								supportingText="Key fields don't align"
								title="Data Mismatch"
								value={ehrValidation.dataMismatch}
							/>
							<MetricCard
								isLoading={isLoading}
								onClick={() => navigate("/validate?status=Missing%20in%20TeleTrace")}
								supportingText="No matching visit or form"
								title="Missing in TeleTrace"
								value={ehrValidation.missingInTeleTrace}
							/>
						</div>
						{!isLoading && (ehrValidation.unconfirmed > 0 || ehrValidation.unableToValidate > 0) ? (
							<div className={classes.gridFive} style={{ marginTop: 14 }}>
								{ehrValidation.unconfirmed > 0 ? (
									<MetricCard
										isLoading={false}
										onClick={() => navigate("/validate?status=Unconfirmed")}
										supportingText="Included in review total"
										title="Unconfirmed"
										value={ehrValidation.unconfirmed}
									/>
								) : null}
								{ehrValidation.unableToValidate > 0 ? (
									<MetricCard
										isLoading={false}
										onClick={() => navigate("/validate?status=Unable%20to%20Validate")}
										supportingText="Identity mapping required"
										title="Unable to Validate"
										value={ehrValidation.unableToValidate}
									/>
								) : null}
							</div>
						) : null}
					</section>

					<section className={classes.section}>
						<div className={classes.sectionHeading}>
							<div className={`${classes.sectionIcon} ${classes.sectionIconAccent}`} aria-hidden="true">
								<svg fill="none" height="16" viewBox="0 0 24 24" width="16">
									<path
										d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Zm4 18H6V4h7v5h5v11Z"
										fill="#ffffff"
									/>
								</svg>
							</div>
							<h2 className={classes.sectionTitle}>Electronic Encounter Forms</h2>
						</div>
						<div className={classes.gridThree}>
							<MetricCard
								isLoading={isLoading}
								onClick={() => navigate("/encounter-forms")}
								supportingText={periodLabel}
								title="Total Forms"
								value={encounterForms.totalForms}
							/>
							<MetricCard
								isLoading={isLoading}
								onClick={() => navigate("/encounter-forms?status=Signed")}
								supportingText={`${encounterForms.signatureRate}% signature rate`}
								title="Signed"
								value={encounterForms.signed}
							/>
							<MetricCard
								isLoading={isLoading}
								onClick={() => navigate("/encounter-forms?status=Pending%20Signature")}
								supportingText="Requires follow-up"
								title="Pending Signature"
								value={encounterForms.pendingSignature}
							/>
						</div>
					</section>
				</>
			)}
		</div>
	);
};

export default Dashboard;
